import { useMemo } from 'react';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import dayjs, { Dayjs } from 'dayjs';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { FilterOption } from '@elementary/common-ui/components';
import {
  DATA_HEALTH_DAYS_BACK,
  DATA_HEALTH_FILTERS,
} from '@elementary/common-ui/constants/urlParams';
import useResourcesMetadataOptions from 'shared/hooks/useResourcesMetadataOptions';
import { DEFAULT_DAYS_BACK } from '@elementary/common-ui/constants';

const useDataHealthFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { urlFilters, clearAllFilters, addFilter, updateFilter, clearFilter } =
    useUrlFilters({
      urlKey: DATA_HEALTH_FILTERS,
    });
  const {
    tags: tagFilterOptions,
    owners: ownerFilterOptions,
    subscribers: subscriberFilterOptions,
    names: namesFilterOptions,
    isCritical,
    databases: databaseFilterOptions,
    schemas: schemaFilterOptions,
  } = useResourcesMetadataOptions();

  const selectedDate = dayjs().subtract(
    Number(searchParams.get(DATA_HEALTH_DAYS_BACK) ?? DEFAULT_DAYS_BACK),
    'days',
  );

  const handleDaysBackChange = (date: Dayjs | undefined) => {
    setSearchParams((prev) => {
      prev.set(DATA_HEALTH_DAYS_BACK, String(dayjs().diff(date, 'days')));
      return prev;
    });
  };

  const filterOptions: FilterOption[] = useMemo(
    () => [
      {
        propertyLabel: 'Tags',
        property: 'tag',
        options: tagFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },
      {
        propertyLabel: 'Owner',
        property: 'owner',
        options: ownerFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },
      {
        propertyLabel: 'Asset name',
        property: 'name',
        options: namesFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },

      {
        propertyLabel: 'Subscriber',
        property: 'subscriber',
        options: subscriberFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },
      {
        propertyLabel: 'Path',
        property: 'path',
        options: [],
        operators: ['contains', 'notContains'],
      },
      {
        propertyLabel: 'Critical Assets',
        property: 'is_critical',
        operators: ['equals'],
        multiselect: false,
        options: isCritical.map((option) => ({
          value: String(option),
          label: option ? 'True' : 'False',
        })),
      },
      {
        propertyLabel: 'Database',
        property: 'database_name',
        options: databaseFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },
      {
        propertyLabel: 'Schema',
        property: 'schema_name',
        options: schemaFilterOptions.map((o) => ({ value: o, label: o })),
        operators: ['equals', 'notEquals'],
      },
    ],
    [
      isCritical,
      namesFilterOptions,
      ownerFilterOptions,
      subscriberFilterOptions,
      tagFilterOptions,
      databaseFilterOptions,
      schemaFilterOptions,
    ],
  );

  return {
    urlFilters,
    filterOptions,
    addFilter,
    clearFilter,
    updateFilter,
    clearAllFilters,
    urlFiltersKey: DATA_HEALTH_FILTERS,
    selectedDate,
    daysBack:
      searchParams.get(DATA_HEALTH_DAYS_BACK) ?? String(DEFAULT_DAYS_BACK),
    handleDaysBackChange,
  };
};

export default useDataHealthFilters;
